import axios from "axios";
import {loginRequest, msalInstance} from "../authConfig";

const baseUrl = process.env.REACT_APP_BACKEND_URL;

export const AxiosInstance = axios.create({
  baseURL: baseUrl,
  timeout: process.env.REACT_APP_TIMEOUT || 30000, // Default timeout of 30 seconds

  headers: {
    "Content-Type": "application/json",
    accept: "application/json",
  },
});

AxiosInstance.interceptors.request.use(async (config) => {
  if (!msalInstance) return config;
  try {
    // msalInstance.initialize() is awaited once in index.js before the app
    // renders, so by the time any request fires the instance is already
    // initialized and reading the same account cache MsalProvider set up.
    const account =
      msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
    if (account) {
      try {
        const tokenResponse = await msalInstance.acquireTokenSilent({
          scopes: loginRequest.scopes,
          account,
        });
        config.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
      } catch {
        await msalInstance.acquireTokenRedirect({
          scopes: loginRequest.scopes,
          account,
        });
      }
    }
  } catch (e) {
    console.warn(
      "MSAL not ready, sending request without auth token:",
      e.message,
    );
  }
  return config;
});

// Add response interceptor for better error logging
AxiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("API Error Details:", {
      message: error.message,
      config: error.config,
      status: error?.response?.status,
      data: error?.response?.data,
      baseURL: error.config?.baseURL,
    });
    return Promise.reject(error);
  },
);

export async function fetchSecurityInfo() {
  try {
    let response = await AxiosInstance.get("SecurityInfo/");
    return response?.data;
  } catch (error) {
    console.error(`Error fetchSecurityInfo: ${error}`);
  }
}

export async function fetchSecurityInfoById(id) {
  try {
    let response = await AxiosInstance.get(`SecurityInfo/${id}`);
    return response?.data;
  } catch (error) {
    console.error(`Error fetchSecurityInfoById: ${error}`);
  }
}

export async function createSecurityInfo(data) {
  try {
    let response = await AxiosInstance.post(`SecurityInfo/`, data);
    return response?.data;
  } catch (error) {
    console.error(`Error createSecurityInfo: ${error}`);
  }
}

export async function updateSecurity(id, data) {
  try {
    let response = await AxiosInstance.put(`SecurityInfo/${id}/`, data);
    return response?.data;
  } catch (error) {
    console.error(`Error updateSecurity: ${error}`);
  }
}

export async function deleteSecurity(id) {
  try {
    let response = await AxiosInstance.delete(`SecurityInfo/${id}/`);
    return response?.data;
  } catch (error) {
    console.error(`Error deleteSecurity: ${error}`);
    throw error;
  }
}

export async function fetchPositions() {
  try {
    // Reads the materialized snapshot (refreshed every 30s by a worker) instead
    // of the live-compute "positions/" endpoint. Rollback: change back to "positions/".
    let response = await AxiosInstance.get("positions-latest/");
    return response?.data;
  } catch (error) {
    console.error(`Error fetchPositions: ${error}`);
  }
}

export async function fetchStratgyAllowedRegions() {
  try {
    let response = await AxiosInstance.get("strategy-allowed-region/");
    return response?.data || [];
  } catch (error) {
    console.error(`Error fetchStratgyAllowedRegions: ${error}`);
    return [];
  }
}

export async function fetchStratgyRegions() {
  try {
    let response = await AxiosInstance.get("strategy-region/");
    return response?.data;
  } catch (error) {
    console.error(`Error fetchStratgyRegions: ${error}`);
  }
}

export async function fetchStrategyRegionsById(id) {
  try {
    let response = await AxiosInstance.get(`strategy-region/${id}`);
    return response?.data;
  } catch (error) {
    console.error(`Error fetchStrategyRegionsById: ${error}`);
  }
}

export async function createStrategyRegions(data) {
  try {
    let response = await AxiosInstance.post(`strategy-region/`, data);
    return response?.data;
  } catch (error) {
    console.error(`Error createStrategyRegions: ${error}`);
  }
}

export async function updateStrategyRegions(id, data) {
  try {
    let response = await AxiosInstance.put(`strategy-region/${id}/`, data);
    return response?.data;
  } catch (error) {
    console.error(`Error updateStrategyRegions: ${error}`);
  }
}

export async function deleteStrategyRegions(id) {
  try {
    let response = await AxiosInstance.delete(`strategy-region/${id}/`);
    return response?.data;
  } catch (error) {
    console.error(`Error deleteStrategyRegions: ${error}`);
    throw error;
  }
}

export async function fetchConvertAttributesInfo() {
  try {
    let response = await AxiosInstance.get(`convert-attributes-info/`);
    return response?.data;
  } catch (error) {
    console.error(`Error fetchConvertAttributesInfo: ${error}`);
    throw error;
  }
}

export async function createConvertAttributesInfo(data) {
  try {
    let response = await AxiosInstance.post(`convert-attributes-info/`, data);
    return response?.data;
  } catch (error) {
    console.error(`Error createConvertAttributesInfo: ${error}`);
    throw error;
  }
}

export async function updateConvertAttributesInfo(id, data) {
  try {
    let response = await AxiosInstance.put(
      `convert-attributes-info/${id}/`,
      data,
    );
    return response?.data;
  } catch (error) {
    console.error(`Error updateConvertAttributesInfo: ${error}`);
    throw error;
  }
}

export async function deleteConvertAttributesInfo(id) {
  try {
    let response = await AxiosInstance.delete(`convert-attributes-info/${id}/`);
    return response?.data;
  } catch (error) {
    console.error(`Error deleteConvertAttributesInfo: ${error}`);
    throw error;
  }
}

export async function fetchConvertsScannerInfo() {
  try {
    let response = await AxiosInstance.get(`converts-scanner-info/`);
    return response?.data;
  } catch (error) {
    console.error(`Error fetchConvertsScannerInfo: ${error}`);
    throw error;
  }
}

export async function fetchConvertsScannerLatestDateData() {
  try {
    let response = await AxiosInstance.get(
      `converts-scanner-info/latest-date-data/`,
    );
    return response?.data;
  } catch (error) {
    console.error(`Error fetchConvertsScannerLatestDateData: ${error}`);
    throw error;
  }
}

export async function fetchPreviousDayPositions() {
  try {
    let response = await AxiosInstance.get("positions_archive/latest-day");
    return response?.data;
  } catch (error) {
    console.error(`Error fetchPreviousDayPositions: ${error}`);
  }
}

/**
 * Fetch home summary (aggregated PnL by strategy). Optional: date (YYYY-MM-DD) for flash PnL.
 * @param {{ date?: string, include_positions?: boolean, include_previous_day?: boolean }} options
 */
export async function fetchHomeSummary(options = {}) {
  try {
    const params = new URLSearchParams();
    if (options.date) params.append("date", options.date);
    if (options.include_positions) params.append("include_positions", "true");
    if (options.include_previous_day)
      params.append("include_previous_day", "true");
    const url = params.toString()
      ? `home-summary/?${params.toString()}`
      : "home-summary/";
    const response = await AxiosInstance.get(url);
    return response?.data;
  } catch (error) {
    console.error(`Error fetchHomeSummary: ${error}`);
    throw error;
  }
}

/**
 * Fetch list of archive dates available for Flash PnL date selector.
 * @returns {{ dates: string[] }}
 */
export async function fetchFlashPnlAvailableDates() {
  try {
    const response = await AxiosInstance.get(
      "positions_archive/available-dates/",
    );
    return response?.data ?? { dates: [] };
  } catch (error) {
    console.error(`Error fetchFlashPnlAvailableDates: ${error}`);
    return { dates: [] };
  }
}

export async function fetchConvertNukes() {
  try {
    let response = await AxiosInstance.get("convert_nukes");
    return response?.data;
  } catch (error) {
    console.error(`Error fetchConvertNukes: ${error}`);
  }
}

export async function fetchArbMonitor() {
  try {
    let response = await AxiosInstance.get("ARB_Monitor_Info");
    return response?.data;
  } catch (error) {
    console.error(`Error fetchArbMonitor: ${error}`);
  }
}

export async function fetchBondMonitor() {
  try {
    let response = await AxiosInstance.get("Bond_Monitor_Info");
    return response?.data;
  } catch (error) {
    console.error(`Error fetchBondMonitor: ${error}`);
  }
}

export async function fetchSpacMonitor() {
  try {
    let response = await AxiosInstance.get("SPAC_Monitor_Info");
    return response?.data;
  } catch (error) {
    console.error(`Error fetchSpacMonitor: ${error}`);
  }
}

export async function fetchSpacRiskData() {
  try {
    let response = await AxiosInstance.get("spac-risk-data/");
    return response;
  } catch (error) {
    console.error(`Error fetchSpacRiskData: ${error}`);
    throw error;
  }
}

export async function fetchSpacLiquidationSummary() {
  try {
    let response = await AxiosInstance.get("spac-risk-liquidation-summary");
    return response?.data?.data || response?.data || [];
  } catch (error) {
    console.error(`Error fetchSpacLiquidationSummary: ${error}`);
    throw error;
  }
}

export async function fetchLatestAlerts() {
  try {
    let response = await AxiosInstance.get("alerts/latest");
    return response?.data?.data;
  } catch (error) {
    console.error(`Error fetchAlerts: ${error}`);
  }
}

export async function fetchSodPositions() {
  try {
    let response = await AxiosInstance.get("sod-positions");
    return response?.data?.data || response?.data || [];
  } catch (error) {
    console.error(`Error fetchSodPositions: ${error}`);
    throw error;
  }
}

export async function fetchMissingTickers() {
  try {
    let response = await AxiosInstance.get("missing-prices");
    return response?.data?.data || response?.data || [];
  } catch (error) {
    console.error(`Error fetchMissingTickers: ${error}`);
    throw error;
  }
}

export async function fetchPositionById(id) {
  try {
    let response = await AxiosInstance.get(`sod-positions/${id}`);
    return response?.data;
  } catch (error) {
    console.error(`Error fetchPositionById: ${error}`);
  }
}

export async function createPosition(positionData) {
  try {
    let response = await AxiosInstance.post(`sod-positions/`, positionData);
    return response?.data;
  } catch (error) {
    console.error(`Error createPosition: ${error}`);
  }
}

export async function updatePosition(id, positionData) {
  try {
    let response = await AxiosInstance.put(
      `sod-positions/${id}/`,
      positionData,
    );
    return response?.data;
  } catch (error) {
    console.error(`Error createPosition: ${error}`);
  }
}

export async function deletePosition(id) {
  try {
    let response = await AxiosInstance.delete(`sod-positions/${id}/`);
    return response?.data;
  } catch (error) {
    console.error(`Error deletePosition: ${error}`);
  }
}

// Converts Blotter API functions
export async function fetchConvertsBlotter() {
  try {
    let response = await AxiosInstance.get("converts-blotter");
    return response?.data?.data || response?.data || [];
  } catch (error) {
    console.error(`Error fetchConvertsBlotter: ${error}`);
    throw error;
  }
}

export async function fetchBlotterEntryById(id) {
  try {
    let response = await AxiosInstance.get(`converts-blotter/${id}`);
    return response?.data;
  } catch (error) {
    console.error(`Error fetchBlotterEntryById: ${error}`);
  }
}

export async function createBlotterEntry(blotterData) {
  try {
    let response = await AxiosInstance.post(`converts-blotter/`, blotterData);
    return response?.data;
  } catch (error) {
    console.error(`Error createBlotterEntry: ${error}`);
  }
}

export async function updateBlotterEntry(id, blotterData) {
  try {
    let response = await AxiosInstance.put(
      `converts-blotter/${id}/`,
      blotterData,
    );
    return response?.data;
  } catch (error) {
    console.error(`Error updateBlotterEntry: ${error}`);
  }
}

export async function deleteBlotterEntry(id) {
  try {
    let response = await AxiosInstance.delete(`converts-blotter/${id}/`);
    return response?.data;
  } catch (error) {
    console.error(`Error deleteBlotterEntry: ${error}`);
  }
}

export async function submitConvertBlotterTrades(rows, mode) {
  // Done trades can take up to ~90s backend-side (60s proxy-to-Eze timeout
  // plus token-refresh overhead) — the default 30s timeout would give up
  // before the backend responds, matching the file-upload/refresh-operation
  // timeout tier already used elsewhere in this file.
  let response = await AxiosInstance.post(
    `submit-convert-trades/?mode=${mode}`,
    rows,
    { timeout: process.env.REACT_APP_TIMEOUT || 120000 },
  );
  return response.data;
}

export async function fetchConvertStrategies() {
  try {
    let response = await AxiosInstance.get("convert-strategies/");
    return response?.data;
  } catch (error) {
    console.error(`Error fetchConvertStrategies: ${error}`);
  }
}

export async function submitManualConvertTrade(payload) {
  // Same reasoning as submitConvertBlotterTrades above — a done trade can
  // take up to ~90s backend-side.
  let response = await AxiosInstance.post("manual-convert-trade/", payload, {
    timeout: process.env.REACT_APP_TIMEOUT || 120000,
  });
  return response?.data;
}

export async function fetchEzeEcdScrubData() {
  try {
    let response = await AxiosInstance.get("eze-ecd-scrub-data");
    return response?.data;
  } catch (error) {
    console.error(`Error fetchEzeEcdScrubData: ${error}`);
    throw error;
  }
}

export async function fetchGenevaEcdScrubData() {
  try {
    let response = await AxiosInstance.get("geneva-ecd-scrub-data");
    return response?.data;
  } catch (error) {
    console.error(`Error fetchGenevaEcdScrubData: ${error}`);
    throw error;
  }
}

// Geneva Report Data Service (proxied through Django backend)
export async function fetchGenevaReportData() {
  try {
    let response = await AxiosInstance.get("geneva-proxy/get_all_report_data");
    return response?.data;
  } catch (error) {
    console.error(`Error fetchGenevaReportData: ${error}`);
    throw error;
  }
}

export async function postGenevaReportRunRequestByAlias(report_alias) {
  try {
    let response = await AxiosInstance.post(
      `geneva-proxy/run_by_report_alias?report_alias=${encodeURIComponent(report_alias)}`,
    );
    return response?.data;
  } catch (error) {
    console.error(`Error postGenevaReportRunRequestByAlias: ${error}`);
    throw error;
  }
}

// ENHANCED: Search historical trades with filters and optional pagination
function buildTradeArchiveParams(query, filters = {}) {
  const params = new URLSearchParams();

  // Add search term if provided
  if (query && query.trim().length >= 3) {
    params.append("search", query.trim());
  }

  // Add filter params
  if (filters.strategyType) {
    params.append("strategy_type", filters.strategyType);
  }
  if (filters.posAction) {
    params.append("pos_action", filters.posAction);
  }
  if (filters.secType) {
    params.append("sec_type", filters.secType);
  }
  if (filters.brokerCode) {
    params.append("broker_code", filters.brokerCode);
  }
  if (filters.dateFrom) {
    params.append("date_from", filters.dateFrom);
  }
  if (filters.dateTo) {
    params.append("date_to", filters.dateTo);
  }
  return params;
}

// Quantity weighted average price per symbol and action over ALL trades
// matching the same query/filters as searchHistoricalTrade (not one page).
export async function getTradeAverages(query, filters = {}) {
  try {
    const params = buildTradeArchiveParams(query, filters);
    const response = await AxiosInstance.get(
      `trades-data-final-archive/averages/?${params.toString()}`,
    );
    return {
      results: response?.data?.results || [],
      totalTrades: response?.data?.total_trades ?? 0,
    };
  } catch (error) {
    console.error(`Error getTradeAverages: ${error}`);
    throw error;
  }
}

// Average price per trade date for a few symbols (the legs of a spread),
// over the same query/filters. Rows: { date, symbol, avg_price, quantity }.
export async function getTradeAveragesByDate(query, filters = {}, symbols = []) {
  try {
    const params = buildTradeArchiveParams(query, filters);
    params.append("by_date", "1");
    symbols.forEach((symbol) => params.append("symbol", symbol));
    const response = await AxiosInstance.get(
      `trades-data-final-archive/averages/?${params.toString()}`,
    );
    return response?.data?.results || [];
  } catch (error) {
    console.error(`Error getTradeAveragesByDate: ${error}`);
    throw error;
  }
}

export async function searchHistoricalTrade(query, filters = {}, options = {}) {
  try {
    const params = buildTradeArchiveParams(query, filters);

    // Add ordering
    params.append("ordering", "-archive_date");

    // Pagination
    if (options.page != null) {
      params.append("page", options.page);
    }
    if (options.pageSize != null) {
      params.append("page_size", options.pageSize);
    }

    const response = await AxiosInstance.get(
      `trades-data-final-archive/?${params.toString()}`,
    );

    const data = response?.data;
    if (data && typeof data.count === "number" && Array.isArray(data.results)) {
      return { results: data.results, totalCount: data.count };
    }
    const list = Array.isArray(data?.results)
      ? data.results
      : Array.isArray(data)
        ? data
        : [];
    return { results: list, totalCount: list.length };
  } catch (error) {
    console.error(`Error searchHistoricalTrade: ${error}`);
    throw error;
  }
}

// NEW: Query historical trades using POST (for complex queries)
export async function queryHistoricalTrades(
  searchTerm = "",
  filters = {},
  options = {},
) {
  try {
    const requestBody = {
      search: searchTerm,
      filters: {
        strategy_type: filters.strategyType || undefined,
        pos_action: filters.posAction || undefined,
        sec_type: filters.secType || undefined,
        broker_code: filters.brokerCode || undefined,
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
      },
      ordering: options.ordering || ["-archive_date", "-created_at"],
      page_size: options.pageSize || 1000,
      page: options.page || 1,
    };

    // Remove undefined values
    Object.keys(requestBody.filters).forEach((key) => {
      if (requestBody.filters[key] === undefined) {
        delete requestBody.filters[key];
      }
    });

    const response = await AxiosInstance.post(
      `trades-data-final-archive/query/`,
      requestBody,
    );

    return response?.data;
  } catch (error) {
    console.error(`Error queryHistoricalTrades: ${error}`);
    throw error;
  }
}

// NEW: Get filter options for dropdowns
export async function getTradeFilterOptions() {
  try {
    const response = await AxiosInstance.get(
      `trades-data-final-archive/filter-options/`,
    );
    return response?.data;
  } catch (error) {
    console.error(`Error getTradeFilterOptions: ${error}`);
    throw error;
  }
}

// Reg M PreClear
export async function checkRegMPreClear({ security, pricing_date, filing_date, exact = false }) {
  try {
    const response = await AxiosInstance.post(`regm-preclear/check/`, {
      security,
      pricing_date,
      filing_date,
      exact,
    });
    return response?.data;
  } catch (error) {
    console.error(`Error checkRegMPreClear: ${error}`);
    throw error;
  }
}

export async function suggestRegMSecurities(q) {
  try {
    const response = await AxiosInstance.get(`regm-preclear/suggest/`, {
      params: { q },
    });
    return response?.data;
  } catch (error) {
    console.error(`Error suggestRegMSecurities: ${error}`);
    throw error;
  }
}

export async function attestRegMPreClear({
  security,
  pricing_date,
  filing_date,
  attestation_version,
  accepted,
  exact = false,
}) {
  try {
    const response = await AxiosInstance.post(`regm-preclear/`, {
      security,
      pricing_date,
      filing_date,
      attestation_version,
      accepted,
      exact,
    });
    return response?.data;
  } catch (error) {
    console.error(`Error attestRegMPreClear: ${error}`);
    throw error;
  }
}

export async function fetchRegMPreClearRecords() {
  try {
    const response = await AxiosInstance.get(`regm-preclear/`);
    return response?.data;
  } catch (error) {
    console.error(`Error fetchRegMPreClearRecords: ${error}`);
    throw error;
  }
}

export async function verifyRegMPreClear(id) {
  try {
    const response = await AxiosInstance.get(`regm-preclear/${id}/verify/`);
    return response?.data;
  } catch (error) {
    console.error(`Error verifyRegMPreClear: ${error}`);
    throw error;
  }
}

export async function reviewRegMPreClear(id, { decision, note }) {
  try {
    const response = await AxiosInstance.post(`regm-preclear/${id}/review/`, {
      decision,
      note,
    });
    return response?.data;
  } catch (error) {
    console.error(`Error reviewRegMPreClear: ${error}`);
    throw error;
  }
}

export async function fetchRegMReviews(id) {
  try {
    const response = await AxiosInstance.get(`regm-preclear/${id}/reviews/`);
    return response?.data;
  } catch (error) {
    console.error(`Error fetchRegMReviews: ${error}`);
    throw error;
  }
}

export async function downloadRegMPreClearPdf(id) {
  try {
    const response = await AxiosInstance.get(`regm-preclear/${id}/pdf/`, {
      responseType: "blob",
    });
    return response?.data;
  } catch (error) {
    console.error(`Error downloadRegMPreClearPdf: ${error}`);
    throw error;
  }
}

// Converts Scanner file upload
export async function uploadConvertsScannerFile(file, date) {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("date", date);

    // Create a temporary axios instance with multipart/form-data content type
    const response = await AxiosInstance.post(
      `converts-scanner-info/upload-file/`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: process.env.REACT_APP_TIMEOUT || 60000, // 60 seconds for file upload
      },
    );
    return response?.data;
  } catch (error) {
    console.error(`Error uploadConvertsScannerFile: ${error}`);
    throw error;
  }
}

// Refresh historical fields for Convert Scanner data
export async function refreshConvertsScannerHistoricalFields(date = null) {
  try {
    const data = date ? { date } : {};
    const response = await AxiosInstance.post(
      "converts-scanner-info/refresh-historical-fields/",
      data,
      {
        timeout: process.env.REACT_APP_TIMEOUT || 120000, // 120 seconds for refresh operation
      },
    );
    return response?.data;
  } catch (error) {
    console.error(`Error refreshConvertsScannerHistoricalFields: ${error}`);
    throw error;
  }
}

// Get unique dates for Convert Scanner data
export async function getConvertsScannerUniqueDates() {
  try {
    const response = await AxiosInstance.get(
      "converts-scanner-info/unique-dates/",
      {
        timeout: process.env.REACT_APP_TIMEOUT || 30000,
      },
    );
    return response?.data;
  } catch (error) {
    console.error(`Error getConvertsScannerUniqueDates: ${error}`);
    throw error;
  }
}

// Check if a date exists in Convert Scanner data
export async function checkConvertsScannerDateExists(date) {
  try {
    const response = await AxiosInstance.get(
      `converts-scanner-info/check-date-exists/?date=${date}`,
      {
        timeout: process.env.REACT_APP_TIMEOUT || 30000,
      },
    );
    return response?.data;
  } catch (error) {
    console.error(`Error checkConvertsScannerDateExists: ${error}`);
    throw error;
  }
}

// Delete all records for a specific date in Convert Scanner data
export async function deleteConvertsScannerByDate(date, password) {
  try {
    const response = await AxiosInstance.delete(
      "converts-scanner-info/delete-by-date/",
      {
        data: { date, password },
        timeout: process.env.REACT_APP_TIMEOUT || 30000,
      },
    );
    return response?.data;
  } catch (error) {
    console.error(`Error deleteConvertsScannerByDate: ${error}`);
    throw error;
  }
}

// Get summary statistics for Convert Scanner data
export async function getConvertsScannerSummary() {
  try {
    const response = await AxiosInstance.get("converts-scanner-info/summary/", {
      timeout: process.env.REACT_APP_TIMEOUT || 30000,
    });
    return response?.data;
  } catch (error) {
    console.error(`Error getConvertsScannerSummary: ${error}`);
    throw error;
  }
}

// Get latest date data for Convert Scanner
export async function getConvertsScannerLatestDateData() {
  try {
    const response = await AxiosInstance.get(
      "converts-scanner-info/latest-date-data/",
      {
        timeout: process.env.REACT_APP_TIMEOUT || 30000,
      },
    );
    return response?.data;
  } catch (error) {
    console.error(`Error getConvertsScannerLatestDateData: ${error}`);
    throw error;
  }
}

export async function getConvertsScannerHistoricalData(isin) {
  try {
    const response = await AxiosInstance.get(
      `converts-scanner-info/historical-data/`,
      {
        params: { isin },
        timeout: process.env.REACT_APP_TIMEOUT || 30000,
      },
    );
    return response?.data;
  } catch (error) {
    console.error(`Error getConvertsScannerHistoricalData: ${error}`);
    throw error;
  }
}

// Latest fund NAV — served from the main backend's GlzNavValue table
// (kept current every ~5 min by the update_equity_monitor job), so it uses
// the same bearer token as every other backend call.
export async function fetchLatestNav() {
  const response = await AxiosInstance.get("nav/latest/");
  return response?.data?.nav;
}

// Historical NAV as of a given date — the most recent NAV recorded at or
// before that date (carries forward over weekends/holidays). Returns
// { nav, date, as_of } where as_of is the actual date the value was recorded
// on, which can differ from the requested date — surface both to the user.
export async function fetchNavByDate(date) {
  const response = await AxiosInstance.get("nav/by-date/", {
    params: { date },
  });
  return response?.data;
}
